import { EyeIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { XMarkIcon } from '@heroicons/react/20/solid';

import { useState } from 'react';

import {
  DashboardFilterValues,
  EMPTY_FILTERS,
  FillStatus,
  FilterOptions,
  dashboardFilterCount,
} from '../api/types';
import FilterSegment, { SegmentOption } from './FilterSegment';

import useAuth from '@/hooks/useAuth';

// The approved Calendar filter bar (Figma node 37673:100905, GRAVY-10305).
//
// A full-width horizontal row beneath the Calendar control row, split into two
// icon-anchored groups plus a right-aligned Reset:
//
//   [ ⌄funnel │ Client │ Skills │ Region ]  [ 👁 │ Status │ Fill rate │ Types ]   ✕ Reset filters
//
// It replaces a narrow vertical popover anchored to the Show button that put
// every dimension into one scrollable checkbox list — a materially different
// interaction from the approved design, missing the Status control and Reset
// entirely, and applying on every checkbox change.
//
// ## Authorization
//
// The Client options come from /filters, which is already narrowed to the
// caller's authorized customers, and a client selection can only ever NARROW
// the server-authorized scope (see CalendarWeekService#apply_filters). Nothing
// here can widen a Customer Admin's scope.

type Props = {
  options?: FilterOptions;
  value: DashboardFilterValues;
  onChange: (next: DashboardFilterValues) => void;
};

const idOptions = (
  items: { id: number; name: string | null }[] | undefined,
): SegmentOption[] =>
  (items ?? []).map((i) => ({
    value: String(i.id),
    label: i.name || `#${i.id}`,
  }));

const valueOptions = (
  items: { value: string; label: string }[] | undefined,
): SegmentOption[] => items ?? [];

const toIds = (values: string[]): number[] => values.map(Number);
const toStrings = (ids: number[]): string[] => ids.map(String);

type SegmentId =
  | 'client'
  | 'skills'
  | 'region'
  | 'status'
  | 'fillRate'
  | 'account'
  | 'types';

const CalendarFilterBar = ({ options, value, onChange }: Props) => {
  const { currentAdmin, isCurrentCustomerAdmin } = useAuth();
  // Guarded: `isCurrentCustomerAdmin` reads `.customer` off its argument and
  // throws on null, and `currentAdmin` IS null while the session is still
  // resolving. Unguarded this crashes the whole filter bar on first paint.
  // Falling back to false means the bar shows Client until the role is known,
  // which is the safe direction — a Client Admin sees their own client, never
  // another's, because the options are server-scoped either way.
  const clientAdmin = !!currentAdmin && isCurrentCustomerAdmin(currentAdmin);

  const activeCount = dashboardFilterCount(value);
  // Which control's dropdown is open — at most one, so opening a second closes
  // the first instead of stacking two panels over the calendar.
  const [openId, setOpenId] = useState<SegmentId | null>(null);

  const set = (patch: Partial<DashboardFilterValues>) =>
    onChange({ ...value, ...patch });

  const openProps = (id: SegmentId) => ({
    open: openId === id,
    onOpenChange: (next: boolean) => setOpenId(next ? id : null),
  });

  return (
    <div className="dashv2-fbar" data-testid="calendarFilterBar">
      <div className="dashv2-fbar-groups">
        {/* Funnel group — what the shift IS. */}
        <div className="dashv2-fgroup">
          <span className="dashv2-fgroup-icon" aria-hidden="true">
            <FunnelIcon />
          </span>
          {/* Client for a Tenant Admin, Billing Account for a Client Admin
              (GRAVY-10331) — the same swap the queues already make
              (GRAVY-10316), and what V1 has always shown. Only ONE of the two
              renders: showing both would imply a Client Admin can change the
              client they are looking at, when authorization fixes it. */}
          {clientAdmin ? (
            <FilterSegment
              label="Billing account"
              {...openProps('account')}
              options={idOptions(options?.accounts)}
              value={toStrings(value.accountIds)}
              onChange={(next) => set({ accountIds: toIds(next) })}
              searchable
            />
          ) : (
            <FilterSegment
              label="Client"
              {...openProps('client')}
              options={idOptions(options?.clients)}
              value={toStrings(value.customerIds)}
              onChange={(next) => set({ customerIds: toIds(next) })}
              // GRAVY-10850 — a tenant's client list runs to hundreds of names,
              // so this is the one segment that cannot be scanned by eye. The
              // shorter lists (Skills, Region, Status, …) stay plain.
              searchable
            />
          )}
          <FilterSegment
            label="Skills"
            {...openProps('skills')}
            options={idOptions(options?.skills)}
            value={toStrings(value.skillIds)}
            onChange={(next) => set({ skillIds: toIds(next) })}
          />
          <FilterSegment
            label="Region"
            {...openProps('region')}
            options={idOptions(options?.regions)}
            value={toStrings(value.regionIds)}
            onChange={(next) => set({ regionIds: toIds(next) })}
          />
        </div>

        {/* Eye group — what the shift's state LOOKS like on the card. */}
        <div className="dashv2-fgroup">
          <span className="dashv2-fgroup-icon" aria-hidden="true">
            <EyeIcon />
          </span>
          <FilterSegment
            label="All status"
            {...openProps('status')}
            options={valueOptions(options?.statuses)}
            value={value.status}
            onChange={(next) => set({ status: next })}
          />
          <FilterSegment
            label="All fill rate"
            {...openProps('fillRate')}
            options={valueOptions(options?.fill_rate_bands)}
            value={value.fillStatus}
            onChange={(next) => set({ fillStatus: next as FillStatus[] })}
          />
          <FilterSegment
            label="All types"
            {...openProps('types')}
            options={valueOptions(options?.shift_types)}
            value={value.jobType}
            onChange={(next) => set({ jobType: next })}
            align="right"
          />
        </div>
      </div>

      <button
        type="button"
        className="dashv2-freset"
        // Disabled with nothing applied, per the design's greyed state — a
        // Reset that does nothing reads as a broken control.
        disabled={activeCount === 0}
        onClick={() => {
          setOpenId(null);
          onChange(EMPTY_FILTERS);
        }}
      >
        <XMarkIcon />
        Reset filters
      </button>
    </div>
  );
};

export default CalendarFilterBar;
